import { useRef, useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import register from 'preact-custom-element';
import type { FunctionComponent } from 'preact';

import { getBoolProp } from '../../../../utils/props';
import { t } from '../../../../domain/localisation';
import { useFaceCapture, useCamera } from '../capture-shared/hooks';
import { CameraPreview } from './components/CameraPreview';
import { MediapipeErrorView } from './components/MediapipeErrorView';
import { AlertDisplay } from './components/AlertDisplay';
import { CaptureControls } from './components/CaptureControls';
import { ActiveLivenessOverlay } from './components/ActiveLivenessOverlay';
import { CaptureGuidelines } from './components/CaptureGuidelines';
import { SmileGuideOverlay } from './assets/smile-illustrations';
import { SubmissionView } from './components/SubmissionView';
import { BackNavigation } from './components/BackNavigation';

import { isMobileDevice } from '../../../../domain/device';

import '../../../navigation/src';
import '../../../attribution/PoweredBySmileId';
// Side-effect import: register the standalone submission custom element so
// partners can mount it independently of ESS
// (`<enhanced-smart-selfie-submission>`). The consent screen now ships as its
// own decoupled entry (`@smileid/web-sdk/consent`, `<smileid-consent>`)
// — see lib/components/consent.
import './EnhancedSmartSelfieSubmission';

// ESS owns the in-flow capture experience: a pre-capture guidelines screen
// (the "Capture Guidelines" tiles + active-liveness hero animation), the
// active-liveness capture itself, and the post-capture review screen. Consent
// and post-confirm submission UI are deliberately not part of this element —
// they're shipped as separate custom elements (`<smileid-consent>`,
// `<enhanced-smart-selfie-submission>`) that callers mount around ESS as their
// product flow requires.
type View = 'guidelines' | 'capture' | 'review';

interface Props {
  interval?: number;
  duration?: number;
  'theme-color'?: string;
  'show-navigation'?: string | boolean;
  'allow-agent-mode'?: string | boolean;
  'show-agent-mode-for-tests'?: string | boolean;
  'hide-attribution'?: string | boolean;
  'disable-image-tests'?: string | boolean;
  /** When true, skip the guidelines screen and go straight to capture. */
  'hide-instructions'?: string | boolean;
  /**
   * Render the back button on the guidelines screen. Off by default because
   * guidelines is the first ESS view, so there is nothing within ESS to
   * navigate back to. Hosts that mount ESS after their own prior screen
   * (e.g. a consent step in a KYC / DocV flow) opt in by setting this so
   * the back button surfaces and `selfie-capture.cancelled` fires for the
   * host to handle navigation back to the previous view.
   */
  'show-back-on-guidelines'?: string | boolean;
  /**
   * Partner opt-in (`allow_legacy_selfie_fallback`) for the interval capture.
   * When the face-detection model can't be loaded, smile mode falls back to a
   * timed capture with no face gating only if this is set; otherwise the user
   * gets the setup error with a retry. Ignored in strict mode, which has no
   * interval-capture path.
   */
  'allow-legacy-selfie-fallback'?: string | boolean;
  /**
   * Which liveness mechanic drives the capture. `directions` (default) is
   * the strict-mode active-liveness flow — the user turns their head to
   * follow pose prompts. `smile` runs the same visual base but gates the
   * capture on a smile instead: no pose overlay, and the prompt sequence
   * is hold-still → please-smile → capturing. Wired by the selfie capture
   * wrapper; not a partner-facing config knob.
   */
  'liveness-mode'?: string;
}

const EnhancedSmartSelfieCapture: FunctionComponent<Props> = ({
  interval = 350,
  duration = 2800,
  'theme-color': themeColor = '#001096',
  'show-navigation': showNavigationProp = false,
  'allow-agent-mode': allowAgentModeProp = false,
  'show-agent-mode-for-tests': showAgentModeForTestsProp = false,
  'hide-attribution': hideAttributionProp = false,
  'hide-instructions': hideInstructionsProp = false,
  'show-back-on-guidelines': showBackOnGuidelinesProp = false,
  'allow-legacy-selfie-fallback': allowLegacySelfieFallbackProp = false,
  'liveness-mode': livenessMode = 'directions',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const showNavigation = getBoolProp(showNavigationProp);
  const allowAgentMode = getBoolProp(allowAgentModeProp);
  const showAgentModeForTests = getBoolProp(showAgentModeForTestsProp);
  const hideAttribution = getBoolProp(hideAttributionProp);
  const hideInstructions = getBoolProp(hideInstructionsProp);
  const showBackOnGuidelines = getBoolProp(showBackOnGuidelinesProp);
  const allowLegacySelfieFallback = getBoolProp(allowLegacySelfieFallbackProp);

  // The liveness mechanic. `directions` maps to the hook's strict mode
  // (pose-driven active liveness); `smile` runs the identical view flow
  // with the smile-gated capture instead.
  const useStrictMode = livenessMode !== 'smile';

  const smileCooldown = 300;
  const smileThreshold = 0.25;
  const mouthOpenThreshold = 0.05;
  // Slightly tighter range than the legacy SmartSelfie defaults so the
  // "too far / too close" edge cases actually trigger when the face fills
  // significantly less / more of the oval than expected. The band is wider
  // than 0.34–0.55 because hysteresis combined with the 0.34 floor caused
  // typical webcam framing to lock into a "too far" state, masking every
  // other check downstream of it.
  const minFaceSize = 0.35;
  const maxFaceSize = 0.62;

  // Agent mode is opt-in: the partner enables the toggle via `allow-agent-mode`.
  // Directions (strict) mode always opens on the user-facing camera — the
  // toggle lets an operator switch to the rear camera mid-session. Smile mode
  // preserves the legacy SmartSelfie agent workflow, which starts on the
  // rear (environment) camera when agent mode is allowed.
  const initialFacingMode =
    !useStrictMode && allowAgentMode ? 'environment' : 'user';
  const camera = useCamera(initialFacingMode);

  // Three-view flow: guidelines → capture → review. `hide-instructions`
  // skips the guidelines screen and jumps straight to capture. On confirm,
  // ESS emits `selfie-capture.publish` and hands off to the caller — the
  // screens router (`<selfie-capture-screens>`) or, in the SmartSelfie Auth
  // case, the host product script — which decides what to render next.
  const initialView: View = hideInstructions ? 'capture' : 'guidelines';
  const viewSignal = useRef(signal<View>(initialView)).current;
  const pendingPayload = useRef<any>(null);
  // `handleConfirm` doesn't navigate away from the review view — the host
  // page decides what renders next, and that swap only happens once the
  // dispatched `selfie-capture.publish` event has propagated all the way up
  // to it (see the comment above `handleConfirm`). Switching to 'submitting'
  // unmounts SubmissionView's Confirm/Retake buttons (see SubmissionView's
  // `isReview` gate) so a fast double-tap can't invoke `handleConfirm` twice
  // before the host hides the camera.
  const reviewMode = useRef(signal<'review' | 'submitting'>('review')).current;

  const faceCapture = useFaceCapture({
    videoRef: camera.videoRef,
    canvasRef,
    interval,
    duration,
    smileThreshold,
    mouthOpenThreshold,
    minFaceSize,
    maxFaceSize,
    smileCooldown,
    getFacingMode: () => camera.facingMode,
    useStrictMode,
    allowIntervalCapture: allowLegacySelfieFallback,
    onCaptureComplete: (detail) => {
      // Stash the payload and show the review screen. The user can retake
      // or confirm; only on confirm do we emit `selfie-capture.publish`.
      //
      // Forced-failure completions (e.g. the 120s active-liveness
      // inactivity timeout from the hosted-web page) publish immediately
      // with `detail.forceFailureReason` set so the caller can route to
      // its own failure UI without going through review.
      pendingPayload.current = detail;
      if (detail.forceFailureReason) {
        // Tear down the camera + detection loop explicitly. We're skipping
        // review, so the view stays on 'capture' and the mount effect's
        // cleanup never runs on its own — without this, MediaPipe and the
        // camera stream would keep running silently while the host shows
        // its post-publish UI.
        faceCapture.stopDetectionLoop();
        camera.stopCamera();
        faceCapture.cleanup();
        // Dedicated forced-failure signal for the host. The intermediate
        // `<selfie-capture-screens>` / `<smart-camera-web>` chain only
        // forwards `images` in their re-dispatched `*.publish` events, so
        // `forceFailureReason` would otherwise be invisible at the host
        // layer. We fire this synchronously *before* `selfie-capture.publish`
        // so any host listener can flip its "this is a forced failure" flag
        // in time for the publish handler that follows.
        window.dispatchEvent(
          new CustomEvent('enhanced-smartselfie.force-fail-published', {
            detail: { reason: detail.forceFailureReason },
          }),
        );
        window.dispatchEvent(
          new CustomEvent('selfie-capture.publish', {
            // `reviewedInCapture` tells the screens router this element has
            // already shown its own review step for this publish.
            detail: { ...detail, reviewedInCapture: true },
          }),
        );
        return;
      }
      viewSignal.value = 'review';
    },
  });

  // Whether face detection is actually available. When MediaPipe can't be
  // loaded, smile capture falls back to an interval capture with no face gating;
  // strict mode can't fall back (head-pose detection *is* its liveness
  // mechanic), so it surfaces a retry instead.
  const detectionAvailable = faceCapture.detectionAvailable.value;
  const mediapipeError = faceCapture.mediapipeError.value;
  // Interval capture needs no model, so Continue unlocks as soon as we know
  // we're falling back rather than waiting on a load that will never finish.
  const readyToContinue =
    faceCapture.isMediapipeReady.value || !detectionAvailable;

  // Component-scope so the camera-error Retry button can re-run the full
  // init sequence, mirroring the legacy SmartSelfie behavior.
  const initializeCamera = async () => {
    try {
      await camera.startCamera(initialFacingMode, (cameraName) => {
        const smartCameraWeb = document.querySelector('smart-camera-web');
        smartCameraWeb?.dispatchEvent(
          new CustomEvent('metadata.camera-name', {
            detail: { cameraName },
          }),
        );
      });
    } catch (error) {
      // The shared useCamera sets `cameraError` and rethrows; skip the
      // downstream face-landmarker init — there is no stream to detect on.
      console.error('Camera initialization failed:', error);
      return;
    }
    await camera.checkAgentSupport();
    await faceCapture.initializeFaceLandmarker();

    setTimeout(() => {
      faceCapture.setupCanvas();
      faceCapture.startDetectionLoop();
    }, 500);
  };

  useEffect(() => {
    if (viewSignal.value !== 'capture') return undefined;

    camera.registerCameraSwitchCallback(() => {
      try {
        faceCapture.resetFaceDetectionState();
        faceCapture.setupCanvas();
        faceCapture.stopDetectionLoop();
        faceCapture.startDetectionLoop();
      } catch (error) {
        console.error('Error during camera switch callback:', error);
      }
    });

    initializeCamera();

    return () => {
      faceCapture.stopDetectionLoop();
      camera.stopCamera();
      faceCapture.cleanup();
    };
  }, [viewSignal.value]);

  // Reset scroll-to-top whenever the active view changes. Without this the
  // host page (which now drives scrolling, since each ESS view uses natural
  // document flow) would land on whatever scroll offset the previous view
  // left behind \u2014 e.g. switching from a long Consent page to Instructions
  // would render Instructions already scrolled to the bottom.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [viewSignal.value]);

  useEffect(() => {
    if (faceCapture.hasFinishedCapture.value) {
      const smartCameraWeb = document.querySelector('smart-camera-web');
      smartCameraWeb?.dispatchEvent(
        new CustomEvent('metadata.selfie-capture-end'),
      );
    }
  }, [faceCapture.hasFinishedCapture.value]);

  // Announce which liveness version is active. `0.0.1` historically meant the
  // legacy capture (no face detection); an interval capture is the same
  // proposition, so it keeps that value and analytics continuity. The
  // fallback reason is reported separately so we can see *why* we fell back.
  useEffect(() => {
    const smartCameraWeb = document.querySelector('smart-camera-web');
    smartCameraWeb?.dispatchEvent(
      new CustomEvent('metadata.active-liveness-version', {
        detail: { version: detectionAvailable ? '1.0.0' : '0.0.1' },
      }),
    );
    smartCameraWeb?.dispatchEvent(
      new CustomEvent('metadata.allow-legacy-selfie-fallback', {
        detail: { allow_legacy_selfie_fallback: allowLegacySelfieFallback },
      }),
    );
    if (detectionAvailable || !mediapipeError) return;
    smartCameraWeb?.dispatchEvent(
      new CustomEvent('metadata.mediapipe-fallback-reason', {
        detail: {
          reason:
            mediapipeError === 'unsupported'
              ? 'unsupported_environment'
              : 'init_failed',
        },
      }),
    );
  }, [detectionAvailable, mediapipeError]);

  // Brief "hold still" period at the start of capture: the reference selfie
  // is taken in this window and the active-liveness animation/prompts only
  // appear after it elapses.
  const HOLD_STILL_MS = 1800;
  const [holdStillElapsed, setHoldStillElapsed] = useState(false);
  // Pre-warm the Mediapipe model as soon as ESS mounts so by the time the user
  // finishes reading the guidelines the heavy network work is already done and
  // Continue can be enabled immediately. The hook owns the load, its bounded
  // retries, and the fall-back-or-error decision — see `useFaceCapture`.
  useEffect(() => {
    faceCapture.initializeFaceLandmarker();
  }, []);
  useEffect(() => {
    if (!faceCapture.isCapturing.value) {
      setHoldStillElapsed(false);
      return undefined;
    }
    setHoldStillElapsed(false);
    const id = window.setTimeout(
      () => setHoldStillElapsed(true),
      HOLD_STILL_MS,
    );
    return () => window.clearTimeout(id);
  }, [faceCapture.isCapturing.value]);

  // Pre-capture fallback: enable Start Capture after 5s even if the user
  // hasn't centred their head yet. Quality checks (lighting / blur /
  // distance) only run during active capture, so we don't want partners to
  // get stuck on a permanently-disabled button.
  const PRE_CAPTURE_FALLBACK_MS = 5000;
  const [preCaptureFallbackElapsed, setPreCaptureFallbackElapsed] =
    useState(false);
  useEffect(() => {
    if (viewSignal.value !== 'capture') return undefined;
    if (faceCapture.isCapturing.value) return undefined;
    if (faceCapture.hasFinishedCapture.value) return undefined;
    setPreCaptureFallbackElapsed(false);
    const id = window.setTimeout(
      () => setPreCaptureFallbackElapsed(true),
      PRE_CAPTURE_FALLBACK_MS,
    );
    return () => window.clearTimeout(id);
  }, [
    viewSignal.value,
    faceCapture.isCapturing.value,
    faceCapture.hasFinishedCapture.value,
  ]);

  // Track device orientation. Active-liveness expects the user to hold the
  // device upright — landscape framing puts the face sideways inside the
  // oval, breaks the bounds/pose checks, and produces unusable selfies.
  // We detect both rotated screens (matchMedia) and rotated mobile devices
  // that report orientation through the Screen Orientation API.
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    // Only phones and tablets get the orientation check: desktops can be
    // resized into a landscape ratio and have no rotate gesture to recover.
    if (!isMobileDevice()) return undefined;

    const mql = window.matchMedia('(orientation: landscape)');
    const update = () => setIsLandscape(mql.matches);
    update();
    // Older Safari only supports addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', update);
      else mql.removeListener(update);
    };
  }, []);

  // If the device flips to landscape mid-capture, pause the capture interval
  // immediately so we don't accumulate liveness frames while the user's face
  // is sideways inside the oval. Resume once the device returns to portrait —
  // the capture sequence picks up from where it left off and continues
  // forward without forcing the user to restart.
  useEffect(() => {
    if (!faceCapture.isCapturing.value) return;
    if (isLandscape) {
      faceCapture.pauseCapture();
    } else if (faceCapture.isPaused.value) {
      faceCapture.resumeCapture();
    }
  }, [isLandscape, faceCapture.isCapturing.value]);

  // Host-driven forced-failure path (e.g. the hosted-web 120s active-liveness
  // inactivity timer). The host dispatches `enhanced-smartselfie.force-fail`
  // with a `{ reason }` detail; the hook packages whatever frames have been
  // captured so far and fires `onCaptureComplete` with `forceFailureReason`
  // set, which publishes immediately so the caller can surface its own
  // failure UI.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ reason?: string }>;
      const reason = ce.detail?.reason ?? 'active_liveness_timed_out';
      // Only react while the user is actively in the capture flow; ignore
      // the event if we've already moved on to review/submitting/etc.
      if (viewSignal.value !== 'capture') return;
      faceCapture.forceFailCapture(reason);
    };
    window.addEventListener('enhanced-smartselfie.force-fail', handler);
    return () => {
      window.removeEventListener('enhanced-smartselfie.force-fail', handler);
    };
  }, []);

  const handleConfirm = () => {
    // A fast double-tap on Confirm re-enters this handler before the host
    // page's publish handler has hidden the button — `reviewMode` flips to
    // 'submitting' synchronously on the first tap, which unmounts the
    // Confirm/Retake buttons (see the SubmissionView render below), so a
    // second tap has nothing left to hit.
    if (reviewMode.value === 'submitting') return;
    const payload = pendingPayload.current;
    if (payload) {
      reviewMode.value = 'submitting';
      // Publish and stay on the review screen. The caller — the screens
      // router for KYC/DV/EDV flows, or the SmartSelfie Auth host page —
      // decides what to render next (next form, submitting card, etc.).
      // `reviewedInCapture` marks that this element already showed its own
      // review, so the screens router publishes straight up to the host.
      window.dispatchEvent(
        new CustomEvent('selfie-capture.publish', {
          detail: { ...payload, reviewedInCapture: true },
        }),
      );
    }
  };

  const handleRetake = () => {
    reviewMode.value = 'review';
    pendingPayload.current = null;
    faceCapture.hasFinishedCapture.value = false;
    faceCapture.capturesTaken.value = 0;
    viewSignal.value = 'capture';
  };

  // Back-button navigation: review → retake; capture → guidelines (or
  // cancel when guidelines are hidden); guidelines → cancel. "Cancel"
  // surfaces `selfie-capture.cancelled` so the caller can decide what to do.
  const handleBack = () => {
    const current = viewSignal.value;
    if (current === 'review') {
      // Once a publish has gone out, back navigation must not retake or
      // cancel — the host is already handling the submission.
      if (reviewMode.value === 'submitting') return;
      handleRetake();
      return;
    }
    if (current === 'capture') {
      // Stop the camera/detection loop before leaving the capture view so
      // we don't leak resources while sitting on the guidelines screen.
      faceCapture.stopDetectionLoop();
      camera.stopCamera();
      faceCapture.resetFaceDetectionState();
      // Wipe the in-flight capture session so re-entering capture starts
      // fresh (new pose sequence, prompt 1, empty image buffer).
      faceCapture.stopCapture();
      faceCapture.capturedImages.value = [];
      faceCapture.referencePhoto.value = '';
      faceCapture.poseSequence.value = [];
      faceCapture.currentPoseIndex.value = 0;
      faceCapture.hasFinishedCapture.value = false;
      faceCapture.capturesTaken.value = 0;
      if (!hideInstructions) {
        viewSignal.value = 'guidelines';
        return;
      }
      faceCapture.handleCancel();
      return;
    }
    // Guidelines or anything else: cancel.
    faceCapture.handleCancel();
  };

  // A failed model load is terminal until the user retries whenever there is no
  // interval capture to fall back to: always in strict mode, whose liveness
  // mechanic *is* head-pose detection, and in smile mode unless the partner
  // opted in via `allow-legacy-selfie-fallback`. Both cases leave
  // `detectionAvailable` true, so it doubles as "we are not falling back".
  // Guarded on the pre-publish views so a retry state can never hijack a
  // completed capture's review screen.
  if (mediapipeError && detectionAvailable && viewSignal.value !== 'review') {
    return (
      <MediapipeErrorView
        themeColor={themeColor}
        hideAttribution={hideAttribution}
        retriesExhausted={faceCapture.retriesExhausted.value}
        onRetry={() => {
          faceCapture.retryMediapipe();
        }}
      />
    );
  }

  if (viewSignal.value === 'guidelines') {
    return (
      <CaptureGuidelines
        themeColor={themeColor}
        hideAttribution={hideAttribution}
        isReady={readyToContinue}
        livenessMode={useStrictMode ? 'directions' : 'smile'}
        onContinue={() => {
          viewSignal.value = 'capture';
        }}
        onBack={showNavigation && showBackOnGuidelines ? handleBack : undefined}
      />
    );
  }

  if (viewSignal.value === 'review') {
    const detail = pendingPayload.current;
    const src = detail?.referenceImage || '';
    const mirror = detail?.facingMode === 'user';
    return (
      <SubmissionView
        imageSrc={src}
        mirror={mirror}
        themeColor={themeColor}
        hideAttribution={hideAttribution}
        mode={reviewMode.value}
        onConfirm={handleConfirm}
        onRetake={handleRetake}
        onBack={
          showNavigation && reviewMode.value === 'review'
            ? handleBack
            : undefined
        }
      />
    );
  }

  // Centralised quality-check evaluation: pick the dominant problem and
  // derive both the prompt copy and which side of the oval to colour red.
  // Pre-capture (idle) only nudges the user to centre their head — every
  // other quality check (lighting / blur / proximity) is deferred until
  // active capture begins. During capture, framing/centering is no longer
  // re-evaluated since the head is already locked in.
  const isIdle =
    !faceCapture.isCapturing.value && !faceCapture.hasFinishedCapture.value;
  const isHoldingStill = faceCapture.isCapturing.value && !holdStillElapsed;

  const isFaceCentered =
    faceCapture.faceDetected.value &&
    faceCapture.faceInBounds.value &&
    !faceCapture.faceClippingOval.value;

  // Default to the pre-capture instruction. We deliberately ignore the
  // hook's transient alertTitle here so the prompt never blanks out as
  // detection signals fluctuate — it stays put until capture begins.
  let alertTitle = isIdle
    ? t('selfie.ess.alert.centerFace')
    : faceCapture.alertTitle.value;
  let errorSide: 'top' | 'right' | 'bottom' | 'left' | 'all' | null = null;
  // Whether any active-capture quality check is currently failing. Used to
  // suppress the active-liveness pose animation so the user isn't shown a
  // "turn your head" cue while we still need them to fix lighting/blur/etc.
  let captureCheckFailing = false;

  if (isLandscape) {
    alertTitle = '';
    errorSide = 'all';
    captureCheckFailing = true;
  } else if (isIdle) {
    // Pre-capture: keep the static "Centre your face within the oval frame"
    // instruction. No directional / lighting / blur / distance prompts run
    // until the user taps Start Capture — quality checks are deferred to
    // the active-capture phase.
  } else if (isHoldingStill) {
    alertTitle = t('selfie.ess.alert.holdStill');
  } else if (faceCapture.isCapturing.value) {
    // Active capture: run quality checks. Centering is no longer evaluated
    // here — once capture has started, head turns are expected.
    if (faceCapture.isTooDark.value) {
      alertTitle = t('selfie.ess.alert.tooDark');
      errorSide = 'all';
      captureCheckFailing = true;
    } else if (faceCapture.isTooBlurry.value) {
      errorSide = 'all';
      captureCheckFailing = true;
    } else if (faceCapture.faceProximity.value === 'too-close') {
      alertTitle = t('selfie.ess.alert.tooClose');
      errorSide = 'all';
      captureCheckFailing = true;
    } else if (faceCapture.faceProximity.value === 'too-far') {
      alertTitle = t('selfie.ess.alert.tooFar');
      errorSide = 'all';
      captureCheckFailing = true;
    } else if (faceCapture.faceOffsetDirection.value) {
      // Directional nudges: face is detected but offset from the oval
      // centre by more than the hook's small threshold. Surfaced during
      // capture so the user re-frames before we evaluate pose prompts.
      const dir = faceCapture.faceOffsetDirection.value;
      if (dir === 'top') alertTitle = t('selfie.ess.alert.moveDeviceUp');
      else if (dir === 'bottom')
        alertTitle = t('selfie.ess.alert.moveDeviceDown');
      else if (dir === 'left')
        alertTitle = t('selfie.ess.alert.moveDeviceLeft');
      else if (dir === 'right')
        alertTitle = t('selfie.ess.alert.moveDeviceRight');
      errorSide = dir;
      captureCheckFailing = true;
    } else if (useStrictMode) {
      // Active-liveness pose prompts: override the localised hook copy with
      // the design-spec wording.
      const pose =
        faceCapture.poseSequence.value[faceCapture.currentPoseIndex.value];
      if (pose === 'left') alertTitle = t('selfie.ess.alert.turnHeadLeft');
      else if (pose === 'right')
        alertTitle = t('selfie.ess.alert.turnHeadRight');
      else if (pose === 'up') alertTitle = t('selfie.ess.alert.tiltHeadUp');
    } else if (
      faceCapture.capturesTaken.value < faceCapture.smileCheckpoint.value
    ) {
      // Smile mode, neutral zone: silent frames are being collected — keep
      // the user steady until the smile checkpoint.
      alertTitle = t('selfie.ess.alert.holdStill');
    } else if (
      // Interval capture has no smile score, so it never reaches "capturing" —
      // it keeps asking for a smile while the timer collects frames.
      detectionAvailable &&
      faceCapture.currentSmileScore.value >= smileThreshold &&
      faceCapture.currentMouthOpen.value >= mouthOpenThreshold
    ) {
      // Smiling wide enough — frames are flowing.
      alertTitle = t('selfie.ess.alert.capturing');
    } else {
      alertTitle = t('selfie.ess.alert.smile');
    }
  }

  // The "Please smile…" moment: smile mode, actively capturing past the
  // neutral zone, all quality checks passing, and the user is not yet
  // smiling wide enough. Single source of truth so the face-outline overlay
  // and the `selfie.ess.alert.smile` prompt above can't drift apart. Interval
  // capture can never observe a smile, so it stays in this state.
  const isAwaitingSmile =
    !useStrictMode &&
    faceCapture.isCapturing.value &&
    holdStillElapsed &&
    !captureCheckFailing &&
    faceCapture.capturesTaken.value >= faceCapture.smileCheckpoint.value &&
    (!detectionAvailable ||
      !(
        faceCapture.currentSmileScore.value >= smileThreshold &&
        faceCapture.currentMouthOpen.value >= mouthOpenThreshold
      ));

  // Smile mode only: the oval border fills chin-up as frames are captured,
  // porting the guidelines hero's green guide-oval reveal to the live capture
  // screen (full ring = capture complete). Strict mode keeps the plain border.
  const captureProgress =
    !useStrictMode && faceCapture.totalCaptures.value > 0
      ? faceCapture.capturesTaken.value / faceCapture.totalCaptures.value
      : 0;

  return (
    <div className="smartselfie-capture">
      {showNavigation && (
        <BackNavigation onBack={handleBack} themeColor={themeColor} />
      )}

      {camera.cameraError ? (
        <div className="camera-error" role="alert">
          <p>{camera.cameraError}</p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              initializeCamera();
            }}
          >
            {t('camera.error.retry')}
          </button>
        </div>
      ) : (
        <>
          <CameraPreview
            videoRef={camera.videoRef}
            canvasRef={canvasRef}
            facingMode={camera.facingMode}
            themeColor={themeColor}
            errorSide={errorSide}
            progress={captureProgress}
            overlay={(() => {
              if (isLandscape) {
                return (
                  <ActiveLivenessOverlay
                    pose={null}
                    currentPose={null}
                    isTooDark={false}
                    isLandscape
                  />
                );
              }
              if (faceCapture.isTooDark.value) {
                // Show the too-dark animation as soon as the scene is too dark,
                // even before active capture starts, so the user gets visual
                // guidance matching the alert text.
                return (
                  <ActiveLivenessOverlay
                    pose={null}
                    currentPose={null}
                    isTooDark
                  />
                );
              }
              if (
                useStrictMode &&
                faceCapture.isCapturing.value &&
                holdStillElapsed &&
                !captureCheckFailing
              ) {
                // Pose animation is directions-only; per the v12 design the
                // smile flow shows a clean oval with text prompts.
                return (
                  <ActiveLivenessOverlay
                    pose={
                      faceCapture.poseSequence.value[
                        faceCapture.currentPoseIndex.value
                      ] ?? null
                    }
                    currentPose={faceCapture.currentPose.value}
                    isTooDark={faceCapture.isTooDark.value}
                  />
                );
              }
              if (isAwaitingSmile) {
                // Smile mode: the face-outline guide cues the user to smile,
                // mirroring the pose overlay's role in strict mode.
                return <SmileGuideOverlay />;
              }
              return null;
            })()}
          />

          {/* Alert text + controls + attribution sit naturally below the
          camera preview. */}
          <AlertDisplay alertTitle={alertTitle} themeColor={themeColor} />

          {!faceCapture.isCapturing.value &&
            !faceCapture.hasFinishedCapture.value && (
              <CaptureControls
                isCapturing={faceCapture.isCapturing.value}
                hasFinishedCapture={faceCapture.hasFinishedCapture.value}
                // Pre-capture readiness: face must be centered, OR the 5-second
                // fallback has elapsed. Quality checks no longer gate the Start
                // Capture button — they only run after capture begins.
                isReadyToCapture={
                  (isFaceCentered || preCaptureFallbackElapsed) && !isLandscape
                }
                // ESS strict-mode: never bypass the readiness check. The legacy
                // fallback timer enables the button after 10s even when the
                // face isn't centered, but we want capture to only start once
                // the user is properly framed inside the oval.
                captureButtonFallbackEnabled={false}
                allowAgentMode={allowAgentMode}
                agentSupported={camera.agentSupported}
                showAgentModeForTests={showAgentModeForTests}
                facingMode={camera.facingMode}
                themeColor={themeColor}
                onStartCapture={faceCapture.startCapture}
                onSwitchCamera={camera.switchCamera}
              />
            )}
        </>
      )}

      {!hideAttribution && (
        // @ts-expect-error preact-custom-element types
        <powered-by-smile-id />
      )}

      <style>{`
        * { box-sizing: border-box; }
        :host { display: block; height: 100%; }
        .smartselfie-capture {
          position: relative;
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 6.5rem 1rem 0.5rem;
          font-family: "DM Sans", system-ui, sans-serif;
          background: #F5F7FA;
          overflow: hidden;
        }
        .smartselfie-capture > .ess-back-navigation {
          position: absolute;
          top: 1.5rem;
          left: 1rem;
          z-index: 2;
        }
        .camera-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          flex: 1;
          text-align: center;
          padding: 1rem;
        }
        .camera-error p {
          margin: 0;
          font-size: 1rem;
          color: #151F72;
        }
        .smartselfie-capture > powered-by-smile-id {
          display: block;
          width: 100%;
          margin-top: clamp(0.75rem, 2dvh, 1.25rem);
          padding-top: clamp(0.4rem, 1dvh, 0.6rem);
          background: #F5F7FA;
          flex-shrink: 0;
        }
        button {
          padding: 10px 20px;
          background: ${themeColor || '#001096'};
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }
        button:disabled { background: #ccc; cursor: not-allowed; }
        button.btn-primary {
          background-color: ${themeColor || '#001096'};
          border-radius: 2.5rem;
          color: white;
          border: none;
          height: 3.125rem;
          padding: 0.75rem 1.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
        }
        button.btn-primary:hover { background-color: #2d2b2a; }
        button.btn-primary:disabled { background-color: #666; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

if (!customElements.get('enhanced-smartselfie-capture')) {
  register(
    EnhancedSmartSelfieCapture,
    'enhanced-smartselfie-capture',
    [
      'interval',
      'duration',
      'theme-color',
      'show-navigation',
      'allow-agent-mode',
      'show-agent-mode-for-tests',
      'hide-attribution',
      'disable-image-tests',
      'hide-instructions',
      'show-back-on-guidelines',
      'allow-legacy-selfie-fallback',
      'liveness-mode',
    ],
    { shadow: true },
  );
}

export default EnhancedSmartSelfieCapture;
